/**
 * Eligibility for the "Source voucher flight" picker on a reminder flight.
 *
 * A reminder flight reuses an EARLIER voucher flight's codes + list. A valid source is:
 *   - in the same campaign (the caller passes only sibling flights),
 *   - not the flight being edited,
 *   - not itself a reminder (no chaining — `voucher_source_flight_id` must be empty),
 *   - has a `list_id` to inherit (a reminder picks no segment of its own), and
 *   - actually has vouchers to reuse: voucher-enabled in the durable/current way
 *     (`generate_vouchers`) OR it already minted vouchers (`reusable_voucher_count > 0`).
 *
 * The voucher-count fallback matters for legacy/source flights whose `generate_vouchers`
 * flag is false/null but which DID generate vouchers — relying on the flag alone would
 * hide a real, reusable source. When the count annotation is absent, we fall back to the
 * flag (no regression vs. the original flag-only rule).
 */

export interface ReminderSourceCandidate {
  id: string;
  voucher_source_flight_id?: string | null;
  list_id?: string | null;
  generate_vouchers?: boolean | null;
  // Optional annotation: how many reusable vouchers this flight already minted.
  reusable_voucher_count?: number | null;
}

export function isReminderSourceCandidate(
  f: ReminderSourceCandidate,
  currentFlightId: string | null | undefined,
): boolean {
  if (!f) {
    return false;
  }
  if (currentFlightId && f.id === currentFlightId) {
    return false; // can't be its own source
  }
  if (f.voucher_source_flight_id) {
    return false; // a reminder can't source another reminder (no chaining)
  }
  if (!f.list_id) {
    return false; // no list to inherit → would resolve 0 recipients
  }
  const hasReusableVouchers = (f.reusable_voucher_count ?? 0) > 0;
  return Boolean(f.generate_vouchers) || hasReusableVouchers;
}
